import { ReactNode, Suspense, useEffect, useState } from 'react';

import {
  IconButton as ButtonIcon,
  Box as Flex,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography as Text,
  Tooltip,
} from '@material-ui/core';

import {
  RiDeleteBinLine,
  RiDownloadLine,
  RiExternalLinkLine,
  RiFileCopyLine,
  RiFileLine,
  RiMore2Fill,
  RiPencilLine,
} from '@remixicon/react';

import {
  DateUtils,
  DialogComposite,
  ErrorBoundary,
  Spinner,
  downloadTextFile,
  sanitizeFilename,
  useAlert,
  useDialog,
  useDocumentNavigation,
} from '@spotify/confidence-core-react';
import {
  CollaborativeDocumentProvider,
  CommentDecorationsExtension,
  CommentMark,
  CopyAsMarkdownButton,
  TipTapEditor,
  useCollaborativeDocument,
  useCollaborativeDocumentContext,
} from '@spotify/confidence-editor-react';
import { AttachmentFragment, getTypeOrNull } from '@spotify/confidence-graphql';
import { MarkdownContent } from '@spotify/plugin-confidence-chat-react';
import {
  IdentityChip,
  identityKind,
} from '@spotify/plugin-confidence-iam-react';
import {
  ArtifactCardBase,
  ArtifactIcon,
  ArtifactListItem,
  createArtifactExtension,
  createModifierClickHandler,
  getResourceName,
  isExternalLink,
} from '@spotify/plugin-confidence-projects-react';
import {
  MetricCard as ReportMetricCard,
  MetricTable as ReportMetricTable,
  VariantCard as ReportVariantCard,
} from '@spotify/plugin-confidence-workflows-module-report';

import { projectsPlugin } from '../plugin';
import styles from './DocumentArtifactExtension.module.css';

const reflowSingleLineTables = (markdown: string): string =>
  markdown.replace(/\|(?:[^|\n]*\|)+/g, table => {
    if (table.includes('\n')) return table;
    const separator = table.match(/\|(?::?-+:?\|)+/);
    if (!separator) return table;
    const cols = (separator[0].match(/:?-+:?/g) ?? []).length;
    if (cols === 0) return table;

    const pipesPerRow = cols + 1;
    const rows: string[] = [];
    let rowStart = 0;
    let rowPipes = 0;
    for (let i = 0; i < table.length; i++) {
      if (table[i] !== '|') continue;
      rowPipes++;
      if (rowPipes === pipesPerRow) {
        rows.push(table.slice(rowStart, i + 1).trim());
        rowStart = i + 1;
        rowPipes = 0;
      }
    }
    if (rows.length < 2) return table;
    return `\n\n${rows.join('\n')}\n\n`;
  });

const hasDocumentContent = (content: unknown): boolean =>
  (typeof content === 'string' && content.trim().length > 0) ||
  (content !== null && content !== undefined && typeof content === 'object');

const DocumentArtifactContent = ({
  documentName,
}: {
  documentName: string;
}) => {
  const { collaborativeDocument, isLoading, startPolling, stopPolling } =
    useCollaborativeDocument({
      name: documentName,
    });

  const markdown = collaborativeDocument?.markdown;
  const content = collaborativeDocument?.content;
  const hasContent = hasDocumentContent(content);
  const hasPendingOperation = !!collaborativeDocument?.pendingOperation;
  const isUpdating = hasPendingOperation || (hasContent && !markdown);

  useEffect(() => {
    if (isUpdating) {
      startPolling(2000);
    } else {
      stopPolling();
    }
    return () => stopPolling();
  }, [isUpdating, startPolling, stopPolling]);

  if (isLoading && !collaborativeDocument) {
    return (
      <div className={styles.loading}>
        <Spinner size={24} aria-label="Loading document" />
      </div>
    );
  }

  if (markdown) {
    return (
      <MarkdownContent>{reflowSingleLineTables(markdown)}</MarkdownContent>
    );
  }

  if (typeof content === 'string' && content.trim()) {
    return <MarkdownContent>{reflowSingleLineTables(content)}</MarkdownContent>;
  }

  if (isUpdating) {
    return (
      <div className={styles.loading}>
        <Spinner size={24} aria-label="Loading document" />
      </div>
    );
  }

  return (
    <div className={styles.emptyState}>
      <Text variant="body2" color="textSecondary">
        This document is empty.
      </Text>
    </div>
  );
};

const MetricCard = ReportMetricCard.extend({
  addNodeView() {
    return null;
  },
});

const MetricTable = ReportMetricTable.extend({
  addNodeView() {
    return null;
  },
});

const VariantCard = ReportVariantCard.extend({
  addNodeView() {
    return null;
  },
});

const PREVIEW_EDITOR_EXTENSIONS = [
  CommentMark,
  CommentDecorationsExtension,
  MetricCard,
  MetricTable,
  VariantCard,
];

const DocumentPreviewEditorInner = () => {
  const { collaborativeDocument, isPublished } =
    useCollaborativeDocumentContext();
  const updater = getTypeOrNull(collaborativeDocument?.updater);
  const hasHumanUpdater = updater
    ? identityKind(updater.name) === 'user'
    : false;

  return (
    <Flex
      display="flex"
      flexDirection="column"
      style={{
        gap: 'var(--bui-space-4)',
      }}
    >
      {collaborativeDocument?.updateTime && (
        <Flex
          display="flex"
          alignItems="center"
          style={{
            gap: 'var(--bui-space-2)',
          }}
        >
          <Text variant="body2" color="textSecondary">
            Last edited{' '}
            {DateUtils.xAgo(new Date(collaborativeDocument.updateTime))}
          </Text>
          {hasHumanUpdater && updater && (
            <IdentityChip size="small" identityRef={updater} />
          )}
        </Flex>
      )}
      <TipTapEditor
        readonly={isPublished}
        disableToolbar
        extensions={PREVIEW_EDITOR_EXTENSIONS}
      />
    </Flex>
  );
};

const DocumentPreviewEditor = ({
  documentName,
  renderActions,
}: {
  documentName: string;
  renderActions?: (documentName: string) => ReactNode;
}) => {
  return (
    <ErrorBoundary
      renderFallback={() => (
        <DocumentArtifactContent documentName={documentName} />
      )}
    >
      <Suspense
        fallback={
          <div className={styles.loading}>
            <Spinner size={24} />
          </div>
        }
      >
        <CollaborativeDocumentProvider documentName={documentName}>
          {renderActions?.(documentName)}
          <DocumentPreviewEditorInner />
        </CollaborativeDocumentProvider>
      </Suspense>
    </ErrorBoundary>
  );
};

const isDocumentArtifact = (artifact: AttachmentFragment) =>
  !isExternalLink(artifact.uri) &&
  getResourceName(artifact.uri).startsWith('collaborativeDocuments/');

const useDocumentHref = (documentName: string) => {
  const { buildDocumentHref } = useDocumentNavigation();
  const [, documentId] = documentName.split('/');
  return documentId ? buildDocumentHref(documentId) : undefined;
};

const DocumentArtifactListItem = ({
  artifact,
  icon,
  onSelect,
  onDelete,
  onRename,
}: {
  artifact: AttachmentFragment;
  icon: ReactNode;
  onSelect: () => void;
  onDelete?: () => void;
  onRename?: () => void;
}) => {
  const alert = useAlert();
  const [menuOpened, setMenuOpened] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const handleMenuClose = () => setMenuAnchorEl(null);
  const documentName = getResourceName(artifact.uri);
  const documentHref = useDocumentHref(documentName);
  const { collaborativeDocument } = useCollaborativeDocument({
    name: documentName,
    skip: !menuOpened,
  });

  const dateLabel = new Date(artifact.createTime).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

  const markdownContent =
    collaborativeDocument?.markdown ??
    (typeof collaborativeDocument?.content === 'string'
      ? collaborativeDocument.content
      : undefined);

  const handleCopyMarkdown = () => {
    if (!markdownContent) return;
    navigator.clipboard.writeText(markdownContent).then(
      () =>
        alert.post({
          message: 'Copied to clipboard',
          severity: 'success',
          display: 'transient',
        }),
      () => alert.post({ message: 'Failed to copy', severity: 'error' }),
    );
  };

  const handleDownloadMarkdown = () => {
    if (!markdownContent) return;
    const displayName = artifact.displayName || 'document';
    const filename = `${sanitizeFilename(displayName)}.md`;
    downloadTextFile(markdownContent, filename);
  };

  return (
    <ArtifactListItem.Root
      onClick={createModifierClickHandler(documentHref, onSelect)}
    >
      <ArtifactListItem.Icon>
        <ArtifactIcon>{icon}</ArtifactIcon>
      </ArtifactListItem.Icon>
      <ArtifactListItem.Content
        primary={
          <Text variant="body1" component="div" noWrap>
            {artifact.displayName || 'Untitled document'}
          </Text>
        }
        secondary={
          <Text variant="caption" component="div" color="textSecondary" noWrap>
            Document &middot; {dateLabel}
          </Text>
        }
      />
      <ArtifactListItem.Action>
        <ButtonIcon
          size="small"
          aria-label="Document actions"
          onClick={e => {
            setMenuOpened(true);
            setMenuAnchorEl(e.currentTarget);
          }}
        >
          <RiMore2Fill size={16} />
        </ButtonIcon>
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem
            onClick={() => {
              if (documentHref) {
                window.open(documentHref, '_blank');
              } else {
                onSelect();
              }
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <RiFileLine />
            </ListItemIcon>
            Open full document
          </MenuItem>
          <MenuItem
            disabled={!markdownContent}
            onClick={() => {
              handleCopyMarkdown();
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <RiFileCopyLine />
            </ListItemIcon>
            Copy as Markdown
          </MenuItem>
          <MenuItem
            disabled={!markdownContent}
            onClick={() => {
              handleDownloadMarkdown();
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <RiDownloadLine />
            </ListItemIcon>
            Download as Markdown
          </MenuItem>
          {onRename && (
            <MenuItem
              onClick={() => {
                onRename();
                handleMenuClose();
              }}
            >
              <ListItemIcon>
                <RiPencilLine />
              </ListItemIcon>
              Rename
            </MenuItem>
          )}
          {onDelete && (
            <MenuItem
              style={{ color: 'var(--bui-fg-danger)' }}
              onClick={() => {
                onDelete();
                handleMenuClose();
              }}
            >
              <ListItemIcon>
                <RiDeleteBinLine />
              </ListItemIcon>
              Delete
            </MenuItem>
          )}
        </Menu>
      </ArtifactListItem.Action>
    </ArtifactListItem.Root>
  );
};

const PopOutDocumentButton = ({ documentName }: { documentName: string }) => {
  const documentHref = useDocumentHref(documentName);

  if (!documentHref) {
    return null;
  }

  return (
    <Tooltip title="Open full document">
      <ButtonIcon
        size="small"
        onClick={() => window.open(documentHref, '_blank')}
        aria-label="Pop out document"
      >
        <RiExternalLinkLine size={16} />
      </ButtonIcon>
    </Tooltip>
  );
};

const DocumentArtifactHeaderActions = ({
  artifact,
}: {
  artifact: AttachmentFragment;
}) => <PopOutDocumentButton documentName={getResourceName(artifact.uri)} />;

const DocumentDialogActions = ({ documentName }: { documentName: string }) => {
  return (
    <div className={styles.dialogActions}>
      <PopOutDocumentButton documentName={documentName} />
      <CopyAsMarkdownButton />
    </div>
  );
};

const DocumentArtifactCard = ({
  artifact,
  onDelete,
}: {
  artifact: AttachmentFragment;
  onDelete?: () => void;
}) => {
  const { openDialog, closeDialog } = useDialog();
  const documentName = getResourceName(artifact.uri);

  const handleNavigate = () => {
    openDialog({
      content: (
        <DialogComposite.Root static onClose={closeDialog}>
          <DialogComposite.Content>
            <DialogComposite.CloseButton />
            <DialogComposite.Header>
              <DialogComposite.Title>
                {artifact.displayName || 'Untitled document'}
              </DialogComposite.Title>
            </DialogComposite.Header>
            <DialogComposite.Body className={styles.dialogBody}>
              <DocumentPreviewEditor
                documentName={documentName}
                renderActions={name => (
                  <DocumentDialogActions documentName={name} />
                )}
              />
            </DialogComposite.Body>
          </DialogComposite.Content>
        </DialogComposite.Root>
      ),
      dialogProps: { maxWidth: 'md', fullWidth: true },
    });
  };

  return (
    <ArtifactCardBase
      artifact={artifact}
      onNavigate={handleNavigate}
      onDelete={onDelete}
    />
  );
};

export const DocumentArtifactExtension = projectsPlugin.provide(
  createArtifactExtension({
    id: 'document',
    predicate: isDocumentArtifact,
    icon: () => ({ Icon: RiFileLine, color: '#6366F1' }),
    label: 'Document',
    category: 'Documents',
    render: artifact => (
      <DocumentPreviewEditor documentName={getResourceName(artifact.uri)} />
    ),
    renderHeaderActions: artifact => (
      <DocumentArtifactHeaderActions artifact={artifact} />
    ),
    renderListItem: (artifact, { onSelect, onDelete, onRename, icon }) => (
      <DocumentArtifactListItem
        artifact={artifact}
        icon={icon}
        onSelect={onSelect}
        onDelete={onDelete}
        onRename={onRename}
      />
    ),
    renderCard: (artifact, { onDelete }) => (
      <DocumentArtifactCard artifact={artifact} onDelete={onDelete} />
    ),
  }),
);
